import React, { useEffect, useState } from "react";
import moment from "moment";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import ModalContainer from "../common/ModalContainer";
import CommonFreeTextInput from "../common/inputs/CommonFreeTextInput";
import CommonDropdownInput from "../common/inputs/CommonDropdownInput";
import CommonDatePicker from "../common/inputs/CommonDatePicker";
import CommonTextAreaInput from "../common/inputs/CommonTextAreaInput";
import CommonCountryInput from "../common/inputs/CommonCountryInput";
import CommonPhoneNumberInput from "../common/CommonPhoneNumberInput";
import { useInsuranceApi } from "../../hooks/useInsuranceApi";

function getRequestBody(booking) {
  return (
    booking?.orderData?.orderData?.requestBody ||
    booking?.booking?.orderData?.orderData?.requestBody ||
    booking?.orderData?.requestBody ||
    null
  );
}

function getPersonsInfo(booking) {
  return getRequestBody(booking)?.personsInfo || [];
}

function parsePhone(phone = "") {
  const parts = String(phone).split("-");
  if (parts.length > 1) {
    return {
      countryCode: parts[0] || "234",
      phoneNumber: parts.slice(1).join("") || "",
    };
  }
  return { countryCode: "234", phoneNumber: String(phone).replace(/\D/g, "") };
}

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr"];
const GENDER_OPTIONS = ["Male", "Female"];
const RELATIONSHIP_OPTIONS = [
  "Wife",
  "Husband",
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Son",
  "Daughter",
  "Friend",
  "Other",
];

const SECTION_KEYS = ["personal", "passport", "nextOfKin", "medical"];

const SECTION_FIELDS = {
  personal: [
    "title",
    "gender",
    "firstName",
    "middleName",
    "lastName",
    "countryCode",
    "birthDate",
    "email",
    "phone",
    "city",
    "state",
  ],
  passport: ["passportNumber"],
  nextOfKin: [
    "nextOfKinRelationship",
    "nextOfKinName",
    "nextOfKinContact",
    "nextOfKinAddress",
  ],
  medical: ["medical"],
};

const OPTIONAL_FIELDS = ["middleName", "nin", "occupation", "zipCode"];
const PHONE_FIELDS = ["phone", "nextOfKinContact"];

function getDefaultSectionState() {
  return SECTION_KEYS.reduce((acc, key) => {
    acc[key] = { open: false, editing: false };
    return acc;
  }, {});
}

function createPassengerErrors(person) {
  return {
    title: false,
    gender: false,
    firstName: false,
    middleName: false,
    lastName: false,
    countryCode: false,
    birthDate: false,
    email: false,
    phone: false,
    city: false,
    state: false,
    occupation: false,
    zipCode: false,
    passportNumber: false,
    nin: false,
    nextOfKinRelationship: false,
    nextOfKinName: false,
    nextOfKinContact: false,
    nextOfKinAddress: false,
    medical: (person?.medical || []).map(() => false),
  };
}

/** Mirrors validateField from useInsuranceActions */
function validateField({ value, field }) {
  if (OPTIONAL_FIELDS.includes(field)) return true;

  if (PHONE_FIELDS.includes(field)) {
    const phonePart = String(value || "").split("-")[1];
    if (!phonePart || phonePart === "NONE") return false;
  }

  if (field === "email") {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || ""));
  }

  return Boolean(value);
}

function buildSectionErrors(person, sectionKey) {
  const errors = {};
  const fields = SECTION_FIELDS[sectionKey] || [];

  fields.forEach((field) => {
    if (field === "medical") {
      errors.medical = (person?.medical || []).map(
        (q) => !validateField({ field: q?.question, value: q?.answer }),
      );
      return;
    }
    errors[field] = !validateField({ field, value: person?.[field] });
  });

  return errors;
}

function sectionHasErrors(sectionErrors, sectionKey) {
  if (sectionKey === "medical") {
    return (sectionErrors?.medical || []).some(Boolean);
  }
  return Object.values(sectionErrors || {}).some(Boolean);
}

function PhoneField({ label, value, onChange, disabled, error, onFocus }) {
  const { countryCode, phoneNumber } = parsePhone(value);
  return (
    <div className="flex flex-col w-full text-sm">
      <span
        className={`block text-sm mb-1 ${error ? "text-red-600" : "text-gray-600"}`}
      >
        {label}
      </span>
      <CommonPhoneNumberInput
        countryCode={countryCode}
        phoneNumber={phoneNumber}
        disabled={disabled}
        error={error}
        onFocus={onFocus}
        onChange={({ countryCode: code, phoneNumber: number }) => {
          onChange(`${code}-${number}`);
        }}
      />
    </div>
  );
}

function CollapsibleSectionCard({
  title,
  open,
  editing,
  hasError,
  onToggle,
  onEditToggle,
  children,
}) {
  return (
    <div
      className={`rounded-xl border bg-white overflow-hidden ${
        hasError ? "border-red-400" : "border-[#E8EEF5]"
      }`}
    >
      <div className="flex items-center gap-2 px-4 py-3 bg-[#F8FAFC] border-b border-[#E8EEF5]">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <Icon
            icon="mdi:chevron-down"
            className={`text-xl text-[#6D7C94] shrink-0 transition-transform ${
              open ? "rotate-0" : "-rotate-90"
            }`}
          />
          <span className="text-sm font-semibold text-[#1A1A1A] truncate">
            {title}
          </span>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEditToggle();
          }}
          className={`shrink-0 h-8 px-3 rounded-lg text-xs font-semibold border transition-colors ${
            editing
              ? "bg-[#1E61DC] text-white border-[#1E61DC]"
              : "bg-white text-[#1E61DC] border-[#DBDBDB] hover:bg-[#F0F6FF]"
          }`}
        >
          {editing ? "Done" : "Edit"}
        </button>
      </div>
      {open ? (
        <div
          className={`p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 ${
            editing ? "" : "pointer-events-none opacity-80"
          }`}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export const RebookInsuranceBookingModal = ({
  setOpen,
  open,
  booking,
  onSuccess,
}) => {
  const { bookInsurance } = useInsuranceApi();
  const [passengers, setPassengers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState(getDefaultSectionState);

  useEffect(() => {
    if (!open) return;
    const persons = getPersonsInfo(booking).map((person) => ({
      ...person,
      medical: Array.isArray(person?.medical)
        ? person.medical.map((item) => ({ ...item }))
        : [],
    }));
    setPassengers(persons);
    setErrors(persons.map((person) => createPassengerErrors(person)));
    setActiveIndex(0);
    setSections(getDefaultSectionState());
  }, [open, booking]);

  const clearFieldError = (index, field, medicalIndex) => {
    setErrors((prev) =>
      prev.map((passengerErrors, i) => {
        if (i !== index) return passengerErrors;
        if (field === "medical") {
          return {
            ...passengerErrors,
            medical: (passengerErrors.medical || []).map((item, mi) =>
              mi === medicalIndex ? false : item,
            ),
          };
        }
        return { ...passengerErrors, [field]: false };
      }),
    );
  };

  const updatePassenger = (index, field, value) => {
    clearFieldError(index, field);
    setPassengers((prev) =>
      prev.map((person, i) =>
        i === index ? { ...person, [field]: value } : person,
      ),
    );
  };

  const updateMedicalAnswer = (passengerIndex, medicalIndex, answer) => {
    clearFieldError(passengerIndex, "medical", medicalIndex);
    setPassengers((prev) =>
      prev.map((person, i) => {
        if (i !== passengerIndex) return person;
        const medical = (person.medical || []).map((item, mi) =>
          mi === medicalIndex ? { ...item, answer } : item,
        );
        return { ...person, medical };
      }),
    );
  };

  const toggleSection = (key) => {
    setSections((prev) => ({
      ...prev,
      [key]: { ...prev[key], open: !prev[key].open },
    }));
  };

  const applySectionErrors = (index, sectionKey, sectionErrors) => {
    setErrors((prev) =>
      prev.map((passengerErrors, i) => {
        if (i !== index) return passengerErrors;
        if (sectionKey === "medical") {
          return {
            ...passengerErrors,
            medical: sectionErrors.medical || [],
          };
        }
        return { ...passengerErrors, ...sectionErrors };
      }),
    );
  };

  const handleEditToggle = (key) => {
    const isEditing = sections[key]?.editing;
    if (!isEditing) {
      setSections((prev) => ({
        ...prev,
        [key]: { open: true, editing: true },
      }));
      return;
    }

    const person = passengers[activeIndex];
    const sectionErrors = buildSectionErrors(person, key);
    if (sectionHasErrors(sectionErrors, key)) {
      applySectionErrors(activeIndex, key, sectionErrors);
      setSections((prev) => ({
        ...prev,
        [key]: { open: true, editing: true },
      }));
      enqueueSnackbar("Please fill in all required fields in this section", {
        variant: "warning",
      });
      return;
    }

    applySectionErrors(
      activeIndex,
      key,
      key === "medical"
        ? { medical: (person?.medical || []).map(() => false) }
        : Object.fromEntries(
            SECTION_FIELDS[key].map((field) => [field, false]),
          ),
    );
    setSections((prev) => ({
      ...prev,
      [key]: { ...prev[key], editing: false },
    }));
  };

  const handlePassengerChange = (index) => {
    setActiveIndex(index);
    setSections(getDefaultSectionState());
  };

  const validateAllPassengers = () => {
    let valid = true;
    let firstInvalidPassenger = null;
    const openSections = { ...getDefaultSectionState() };
    const nextErrors = passengers.map((person, index) => {
      const passengerErrors = createPassengerErrors(person);

      SECTION_KEYS.forEach((sectionKey) => {
        const sectionErrors = buildSectionErrors(person, sectionKey);
        if (sectionKey === "medical") {
          passengerErrors.medical = sectionErrors.medical || [];
        } else {
          Object.assign(passengerErrors, sectionErrors);
        }
        if (sectionHasErrors(sectionErrors, sectionKey)) {
          valid = false;
          if (firstInvalidPassenger == null) firstInvalidPassenger = index;
          openSections[sectionKey] = { open: true, editing: true };
        }
      });

      return passengerErrors;
    });

    setErrors(nextErrors);
    if (!valid) {
      setActiveIndex(firstInvalidPassenger ?? 0);
      setSections(openSections);
    }
    return valid;
  };

  const handleRebook = async () => {
    if (!validateAllPassengers()) {
      enqueueSnackbar("Please complete all required traveller details", {
        variant: "warning",
      });
      return;
    }

    const requestBody = getRequestBody(booking);
    if (!requestBody) {
      enqueueSnackbar("Unable to rebook: missing booking request data", {
        variant: "error",
      });
      return;
    }

    const agentAccountId = booking?.account?._id || booking?.account;
    if (!agentAccountId) {
      enqueueSnackbar("Unable to rebook: missing agent account", {
        variant: "error",
      });
      return;
    }

    await bookInsurance({
      setLoading,
      payload: {
        ...requestBody,
        personsInfo: passengers,
        agentAccountId,
      },
      callback: () => {
        setOpen(false);
        onSuccess?.();
      },
    });
  };

  const passenger = passengers[activeIndex];
  const passengerErrors = errors[activeIndex] || createPassengerErrors({});
  const medicalItems = passenger?.medical || [];

  return (
    <ModalContainer
      open={open}
      setOpen={setOpen}
      secondary
      padding="p-4 sm:p-6"
      childClassname="max-w-[720px]"
      title="Rebook insurance"
      subTitle="Update traveller details before rebooking this policy."
    >
      <div className="w-full flex flex-col gap-5">
        {passengers.length > 1 ? (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {passengers.map((person, index) => {
              const name =
                [person?.firstName, person?.lastName]
                  .filter(Boolean)
                  .join(" ") || `Passenger ${index + 1}`;
              const isActive = index === activeIndex;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handlePassengerChange(index)}
                  className={`shrink-0 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    isActive
                      ? "bg-[#1E61DC] text-white border-[#1E61DC]"
                      : "bg-white text-[#6D7C94] border-[#DBDBDB] hover:bg-[#F8FAFC]"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        ) : null}

        {!passenger ? (
          <div className="rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] px-4 py-6 text-sm text-[#6D7C94] text-center">
            No traveller details found on this booking.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="px-1">
              <span className="text-base font-semibold text-[#1A1A1A]">
                Passenger {activeIndex + 1}
                {passenger?.ageCategory ? ` · ${passenger.ageCategory}` : ""}
              </span>
            </div>

            <CollapsibleSectionCard
              title="Personal details"
              open={sections.personal.open}
              editing={sections.personal.editing}
              hasError={sectionHasErrors(
                {
                  title: passengerErrors.title,
                  gender: passengerErrors.gender,
                  firstName: passengerErrors.firstName,
                  lastName: passengerErrors.lastName,
                  countryCode: passengerErrors.countryCode,
                  birthDate: passengerErrors.birthDate,
                  email: passengerErrors.email,
                  phone: passengerErrors.phone,
                  city: passengerErrors.city,
                  state: passengerErrors.state,
                },
                "personal",
              )}
              onToggle={() => toggleSection("personal")}
              onEditToggle={() => handleEditToggle("personal")}
            >
              <CommonDropdownInput
                label="Title"
                options={TITLE_OPTIONS}
                value={passenger?.title || ""}
                placeholder="Select title"
                error={passengerErrors.title}
                onChange={(value) =>
                  updatePassenger(activeIndex, "title", value)
                }
              />
              <CommonFreeTextInput
                label="First name"
                value={passenger?.firstName || ""}
                placeholder="First name"
                error={passengerErrors.firstName}
                onFocus={() => clearFieldError(activeIndex, "firstName")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "firstName", value)
                }
              />
              <CommonFreeTextInput
                label="Middle name"
                value={passenger?.middleName || ""}
                placeholder="Middle name"
                onChange={(value) =>
                  updatePassenger(activeIndex, "middleName", value)
                }
              />
              <CommonFreeTextInput
                label="Last name"
                value={passenger?.lastName || ""}
                placeholder="Last name"
                error={passengerErrors.lastName}
                onFocus={() => clearFieldError(activeIndex, "lastName")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "lastName", value)
                }
              />
              <CommonDropdownInput
                label="Gender"
                options={GENDER_OPTIONS}
                value={passenger?.gender || ""}
                placeholder="Select gender"
                error={passengerErrors.gender}
                onChange={(value) =>
                  updatePassenger(activeIndex, "gender", value)
                }
              />
              <CommonDatePicker
                label="Date of birth"
                value={
                  passenger?.birthDate
                    ? moment(passenger.birthDate).toDate()
                    : null
                }
                placeholder="Select date"
                maxDate={new Date()}
                error={passengerErrors.birthDate}
                onFocus={() => clearFieldError(activeIndex, "birthDate")}
                onChange={(date) =>
                  updatePassenger(
                    activeIndex,
                    "birthDate",
                    date ? moment(date).format("YYYY-MM-DD") : "",
                  )
                }
              />
              <div className="flex flex-col w-full text-sm">
                <span className="block text-sm mb-1 text-gray-600">
                  Age category
                </span>
                <div className="h-12 px-4 rounded-lg border border-[#DBDBDB] bg-[#EEF2F7] text-sm text-[#1A1A1A] flex items-center">
                  {passenger?.ageCategory || "—"}
                </div>
              </div>
              <CommonFreeTextInput
                label="Email"
                type="email"
                value={passenger?.email || ""}
                placeholder="Email"
                error={passengerErrors.email}
                onFocus={() => clearFieldError(activeIndex, "email")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "email", value)
                }
              />
              <PhoneField
                label="Phone"
                value={passenger?.phone || ""}
                disabled={!sections.personal.editing}
                error={passengerErrors.phone}
                onFocus={() => clearFieldError(activeIndex, "phone")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "phone", value)
                }
              />
              <CommonFreeTextInput
                label="Occupation"
                value={passenger?.occupation || ""}
                placeholder="Occupation"
                onChange={(value) =>
                  updatePassenger(activeIndex, "occupation", value)
                }
              />
              <CommonFreeTextInput
                label="City"
                value={passenger?.city || ""}
                placeholder="City"
                error={passengerErrors.city}
                onFocus={() => clearFieldError(activeIndex, "city")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "city", value)
                }
              />
              <CommonFreeTextInput
                label="State"
                value={passenger?.state || ""}
                placeholder="State"
                error={passengerErrors.state}
                onFocus={() => clearFieldError(activeIndex, "state")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "state", value)
                }
              />
              <CommonCountryInput
                label="Country"
                value={passenger?.countryCode || ""}
                placeholder="Select country"
                error={passengerErrors.countryCode}
                onChange={(value) =>
                  updatePassenger(activeIndex, "countryCode", value)
                }
              />
              <CommonFreeTextInput
                label="Zip code"
                value={passenger?.zipCode || ""}
                placeholder="Zip code"
                onChange={(value) =>
                  updatePassenger(activeIndex, "zipCode", value)
                }
              />
            </CollapsibleSectionCard>

            <CollapsibleSectionCard
              title="Passport details"
              open={sections.passport.open}
              editing={sections.passport.editing}
              hasError={!!passengerErrors.passportNumber}
              onToggle={() => toggleSection("passport")}
              onEditToggle={() => handleEditToggle("passport")}
            >
              <CommonFreeTextInput
                label="Passport number"
                value={passenger?.passportNumber || ""}
                placeholder="Passport number"
                error={passengerErrors.passportNumber}
                onFocus={() => clearFieldError(activeIndex, "passportNumber")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "passportNumber", value)
                }
              />
              <CommonFreeTextInput
                label="NIN"
                value={passenger?.nin || ""}
                placeholder="NIN"
                onChange={(value) => updatePassenger(activeIndex, "nin", value)}
              />
            </CollapsibleSectionCard>

            <CollapsibleSectionCard
              title="Next of kin"
              open={sections.nextOfKin.open}
              editing={sections.nextOfKin.editing}
              hasError={sectionHasErrors(
                {
                  nextOfKinRelationship: passengerErrors.nextOfKinRelationship,
                  nextOfKinName: passengerErrors.nextOfKinName,
                  nextOfKinContact: passengerErrors.nextOfKinContact,
                  nextOfKinAddress: passengerErrors.nextOfKinAddress,
                },
                "nextOfKin",
              )}
              onToggle={() => toggleSection("nextOfKin")}
              onEditToggle={() => handleEditToggle("nextOfKin")}
            >
              <CommonFreeTextInput
                label="Full name"
                value={passenger?.nextOfKinName || ""}
                placeholder="Next of kin name"
                error={passengerErrors.nextOfKinName}
                onFocus={() => clearFieldError(activeIndex, "nextOfKinName")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "nextOfKinName", value)
                }
              />
              <CommonDropdownInput
                label="Relationship"
                options={RELATIONSHIP_OPTIONS}
                value={passenger?.nextOfKinRelationship || ""}
                placeholder="Select relationship"
                error={passengerErrors.nextOfKinRelationship}
                onChange={(value) =>
                  updatePassenger(activeIndex, "nextOfKinRelationship", value)
                }
              />
              <PhoneField
                label="Contact"
                value={passenger?.nextOfKinContact || ""}
                disabled={!sections.nextOfKin.editing}
                error={passengerErrors.nextOfKinContact}
                onFocus={() => clearFieldError(activeIndex, "nextOfKinContact")}
                onChange={(value) =>
                  updatePassenger(activeIndex, "nextOfKinContact", value)
                }
              />
              <div className="sm:col-span-2">
                <CommonTextAreaInput
                  label="Address"
                  value={passenger?.nextOfKinAddress || ""}
                  placeholder="Next of kin address"
                  error={passengerErrors.nextOfKinAddress}
                  onFocus={() =>
                    clearFieldError(activeIndex, "nextOfKinAddress")
                  }
                  onChange={(value) =>
                    updatePassenger(activeIndex, "nextOfKinAddress", value)
                  }
                />
              </div>
            </CollapsibleSectionCard>

            <CollapsibleSectionCard
              title="Medical questions"
              open={sections.medical.open}
              editing={sections.medical.editing}
              hasError={(passengerErrors.medical || []).some(Boolean)}
              onToggle={() => toggleSection("medical")}
              onEditToggle={() => handleEditToggle("medical")}
            >
              {medicalItems.length ? (
                medicalItems.map((item, medicalIndex) => (
                  <div key={medicalIndex} className="sm:col-span-2">
                    <CommonTextAreaInput
                      label={
                        item?.question || `Medical question ${medicalIndex + 1}`
                      }
                      value={item?.answer || ""}
                      placeholder="Answer"
                      error={!!passengerErrors.medical?.[medicalIndex]}
                      onFocus={() =>
                        clearFieldError(activeIndex, "medical", medicalIndex)
                      }
                      onChange={(value) =>
                        updateMedicalAnswer(activeIndex, medicalIndex, value)
                      }
                    />
                  </div>
                ))
              ) : (
                <div className="sm:col-span-2 text-sm text-[#6D7C94]">
                  No medical questions for this traveller.
                </div>
              )}
            </CollapsibleSectionCard>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => setOpen(false)}
            disabled={loading}
            className="flex-1 h-12 rounded-lg border border-[#DBDBDB] bg-white text-sm font-semibold text-[#6D7C94] hover:bg-[#F8FAFC] disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRebook}
            disabled={loading || !passengers.length}
            className="flex-1 h-12 rounded-lg bg-[#1E61DC] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center"
          >
            {loading ? <div className="buttonLoader" /> : "Rebook"}
          </button>
        </div>
      </div>
    </ModalContainer>
  );
};
